<script setup>
import { ref, computed, watch } from 'vue';
import { useId } from 'vue';
import Datepicker from '@vuepic/vue-datepicker';
import { format as formatDate, parse } from 'date-fns';

const props = defineProps({
    label: String,
    type: String,
    name: String,
    required: Boolean,
    disabled: Boolean,
    autocomplete: String,
    format: String,
    maxDate: String,
    minDate: String,
});

const model = defineModel();
const id = useId();
const element = ref(null);
let hidePassword = ref(true);
let newPassword = ref(null);

const isDateType = computed(() => props.type === 'date');
const normalizedLabel = computed(() => (props.label || '').replace('*', ''));

const date = ref(null);

watch(
    () => model.value,
    (newValue) => {
        if (isDateType.value) {
            if (newValue) {
                date.value = parse(newValue, props.format || 'dd.MM.yyyy', new Date());
            } else {
                date.value = null;
            }
        }
    },
    { immediate: true }
);

const computedMaxDate = computed(() => {
    if (!props.maxDate) return undefined;
    try {
        return parse(props.maxDate, props.format || 'dd.MM.yyyy', new Date()).toISOString();
    } catch {
        return new Date().toISOString();
    }
});

const computedMinDate = computed(() => {
    if (!props.minDate) return undefined;
    try {
        return parse(props.minDate, props.format || 'dd.MM.yyyy', new Date()).toISOString();
    } catch {
        return undefined;
    }
});

function onDateChange() {
    if (date.value === null) {
        model.value = null;
        return;
    }
    model.value = formatDate(new Date(date.value), props.format || 'dd.MM.yyyy');
}
</script>

<template>
    <div class="field">
        <label :for="id">
            {{ normalizedLabel }}
            <span v-if="required">*</span>
        </label>
        <div v-if="isDateType" class="date-field">
            <datepicker
                auto-apply
                v-model="date"
                :format="format || 'dd.MM.yyyy'"
                placeholder="дд.мм.гггг"
                :min-date="computedMinDate"
                :max-date="computedMaxDate"
                :enable-time-picker="false"
                :config="{ keepActionRow: true }"
                :readonly="disabled"
                :text-input="true"
                :clearable="false"
                :action-row="{ showNow: true, showCancel: false, showSelect: false, showPreview: false }"
                now-button-label="Сегодня"
                month-name-format="long"
                locale="ru"
                utc
                @update:modelValue="onDateChange"
            />
            <svg
                class="date-icon"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M8 2V5M16 2V5M3.5 9.09H20.5M21 8.5V17C21 20 19.5 22 16 22H8C4.5 22 3 20 3 17V8.5C3 5.5 4.5 3.5 8 3.5H16C19.5 3.5 21 5.5 21 8.5Z"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M15.695 13.7H15.704M15.695 16.7H15.704M11.995 13.7H12.005M11.995 16.7H12.005M8.295 13.7H8.305M8.295 16.7H8.305"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
            </svg>
        </div>
        <div v-else :class="{ 'password-field': type === 'password' }">
            <input
                ref="element"
                :id="id"
                :name="name"
                :type="
                    type !== 'password'
                        ? type
                        : hidePassword
                          ? 'password'
                          : 'text'
                "
                :disabled="disabled"
                :required="required"
                :autocomplete="autocomplete"
                v-model="model"
            />
            <button
                type="button"
                class="cancel"
                aria-label="Стереть"
                title="Стереть"
                v-if="
                    !disabled && type !== 'password' && type !== 'date' && model
                "
                @click="((model = ''), element.focus())"
            >
                <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M8.6281 7.27934C8.25565 6.90689 7.65179 6.90689 7.27934 7.27934C6.90689 7.65179 6.90689 8.25565 7.27934 8.6281L10.6512 12L7.27934 15.3719C6.90689 15.7444 6.90689 16.3482 7.27934 16.7207C7.65179 17.0931 8.25565 17.0931 8.6281 16.7207L12 13.3488L15.3719 16.7207C15.7444 17.0931 16.3482 17.0931 16.7207 16.7207C17.0931 16.3482 17.0931 15.7444 16.7207 15.3719L13.3488 12L16.7207 8.6281C17.0931 8.25565 17.0931 7.65179 16.7207 7.27934C16.3482 6.90689 15.7444 6.90689 15.3719 7.27934L12 10.6512L8.6281 7.27934Z"
                    />
                </svg>
            </button>
            <button
                type="button"
                v-if="!disabled && type === 'password'"
                @click="hidePassword = !hidePassword"
                class="password-toggle"
                aria-label="Показать пароль"
                title="Показать пароль"
            >
                <!-- Eye off icon (пароль скрыт) -->
                <svg
                    class="eye-off"
                    v-if="hidePassword"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M2.56095 8.10165C3.05722 7.8593 3.65599 8.06514 3.89834 8.56141C4.28644 9.35612 4.75926 10.0842 5.30163 10.7315C5.32943 10.7606 5.35513 10.791 5.37874 10.8224C6.17012 11.7446 7.10462 12.4975 8.13684 13.0385C8.16411 13.0493 8.19113 13.0614 8.21785 13.0748C8.24882 13.0904 8.27863 13.1073 8.30725 13.1255C9.44451 13.6901 10.695 13.9999 12 13.9999C13.3048 13.9999 14.5553 13.6901 15.6926 13.1255C15.7213 13.1073 15.7511 13.0904 15.7821 13.0748C15.8089 13.0614 15.836 13.0493 15.8633 13.0385C16.8944 12.498 17.828 11.7462 18.6189 10.8254C18.6439 10.7919 18.6713 10.7595 18.7011 10.7286C19.2425 10.0821 19.7145 9.35511 20.1021 8.56169C20.3446 8.06546 20.9434 7.85971 21.4396 8.10214C21.9358 8.34457 22.1416 8.94338 21.8991 9.43961C21.5708 10.1117 21.1878 10.7485 20.7563 11.342L22.207 12.7928C22.5976 13.1834 22.5976 13.8165 22.207 14.207C21.8165 14.5976 21.1834 14.5976 20.7928 14.207L19.4483 12.8625C18.8674 13.4341 18.2321 13.9411 17.5514 14.372L18.3938 16.0516C18.6414 16.5453 18.442 17.1462 17.9483 17.3938C17.4546 17.6414 16.8537 17.4419 16.6061 16.9483L15.7696 15.2804C14.8936 15.6271 13.9657 15.8569 13 15.9513L13 17.9999C13 18.5522 12.5523 18.9999 12 18.9999C11.4477 18.9999 11 18.5522 11 17.9999L11 15.9513C10.0343 15.8569 9.10635 15.6271 8.23037 15.2804L7.39387 16.9483C7.14628 17.4419 6.54536 17.6414 6.05168 17.3938C5.55801 17.1462 5.35853 16.5453 5.60612 16.0516L6.44854 14.372C5.76785 13.941 5.13262 13.434 4.55173 12.8624L3.20711 14.207C2.81658 14.5976 2.18342 14.5976 1.79289 14.207C1.40237 13.8165 1.40237 13.1834 1.79289 12.7928L3.24393 11.3418C2.81245 10.7482 2.42947 10.1113 2.1012 9.43905C1.85884 8.94278 2.06468 8.34401 2.56095 8.10165Z"
                    />
                </svg>
                <!-- Eye on icon (пароль виден) -->
                <svg
                    class="eye-on"
                    v-if="!hidePassword"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M5.61403 6.34903C7.51752 4.96903 9.78322 4 12.0005 4C14.2178 4 16.4832 4.96932 18.3863 6.34948C20.2964 7.73463 21.9433 9.6059 22.8985 11.5613C23.0338 11.8383 23.0338 12.1621 22.8985 12.4391C21.9433 14.3946 20.2962 16.2657 18.3859 17.6508C16.4826 19.0308 14.217 20 11.9998 20C9.78248 20 7.51693 19.0308 5.61362 17.6507C3.70344 16.2656 2.05637 14.3943 1.10141 12.4386C0.966198 12.1617 0.966198 11.8379 1.10141 11.561C2.0564 9.60523 3.70369 7.73401 5.61403 6.34903ZM3.12786 11.9998C3.94752 13.4726 5.25598 14.9209 6.78768 16.0316C8.4782 17.2574 10.3393 18 11.9998 18C13.6602 18 15.5213 17.2574 17.2119 16.0316C18.7436 14.921 20.0522 13.4729 20.8721 12.0002C20.0522 10.5275 18.7438 9.07922 17.2122 7.96856C15.5218 6.74267 13.6609 6 12.0005 6C10.3399 6 8.47864 6.74254 6.78795 7.96826C5.2561 9.07884 3.94752 10.527 3.12786 11.9998ZM12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10ZM8 12C8 9.79086 9.79086 8 12 8C14.2091 8 16 9.79086 16 12C16 14.2091 14.2091 16 12 16C9.79086 16 8 14.2091 8 12Z"
                    />
                </svg>
            </button>
        </div>
    </div>
</template>

<style scoped>
* {
    box-sizing: border-box;
}
.field {
    display: flex;
    flex-direction: column;
    gap: 8px;

    label {
        font-size: 16px;
        line-height: 16px;
        font-weight: 500;

        span {
            color: rgba(255, 50, 14, 1);
        }
    }

    > div {
        width: 100%;
        position: relative;

        &.password-field {
            input {
                padding-right: 56px;
            }

            /* Password toggle UI */

            .password-toggle {
                display: flex;
                position: absolute;
                top: 50%;
                right: 16px;
                transform: translateY(-50%);
                border: none;
                background: transparent;
                padding: 4px;
                margin: 0;
                cursor: pointer;
                color: rgba(142, 142, 157, 1);
            }

            .password-toggle:hover {
                color: rgba(34, 42, 118, 1);
            }

            .password-toggle:focus {
                outline: none;
            }
        }

        input {
            border: 1px solid #d4d3d9;
            border-radius: 12px;
            height: 56px;
            font-family: 'Mulish', sans-serif;
            font-size: 16px;
            font-weight: 400;
            line-height: 16px;
            padding: 8px 16px;
            color: inherit;
            width: 100%;

            &:focus {
                border: 1px solid rgba(34, 42, 118, 1);
                outline: none;
            }

            &:disabled {
                background-color: rgba(235, 235, 238, 1);
                outline: none;
            }
        }

        .cancel {
            display: flex;
            position: absolute;
            top: 50%;
            right: 16px;
            transform: translateY(-50%);
            border: none;
            background: transparent;
            padding: 4px;
            margin: 0;
            cursor: pointer;
            color: rgba(142, 142, 157, 1);

            &:hover {
                color: rgba(34, 42, 118, 1);
            }

            &:focus {
                outline: none;
            }
        }

        input:focus + .cancel {
            color: rgba(34, 42, 118, 1);
        }
    }

    > .date-field {
        width: 100%;
        position: relative;

        :deep(.dp__input) {
            border: 1px solid #d4d3d9;
            border-radius: 12px;
            height: 56px;
            font-family: 'Mulish', sans-serif;
            font-size: 16px;
            font-weight: 400;
            line-height: 16px;
            padding: 8px 48px 8px 16px;
            color: inherit;
            width: 100%;

            &:focus {
                border: 1px solid rgba(34, 42, 118, 1);
                outline: none;
            }
        }

        :deep(.dp__input_icon) {
            display: none;
        }

        :deep(.dp__clear_icon) {
            display: none;
        }

        .date-icon {
            position: absolute;
            top: 50%;
            right: 16px;
            transform: translateY(-50%);
            pointer-events: none;
            color: rgba(142, 142, 157, 1);
        }

        :deep(.dp__menu),
        :deep(.dp__instance_calendar) {
            border-radius: 16px;
            overflow: hidden;
        }

        :deep(.dp__inner_nav),
        :deep(.dp--arrow-btn-nav) {
            display: none !important;
        }

        :deep(.dp__month_year_row) {
            justify-content: center;
            padding: 8px 0;
        }

        :deep(.dp__month_year_wrap) {
            justify-content: center;
            gap: 8px;
            width: 100%;
        }

        :deep(.dp__month_year_select),
        :deep(.dp--year-select) {
            width: auto;
            min-width: auto;
            border: none;
            border-radius: 0;
            background: transparent;
            color: #12122c;
            font-weight: 600;
            font-size: 16px;
            line-height: 1;
            padding: 0;
            display: inline-flex;
            align-items: center;
            gap: 6px;
        }

        :deep(.dp__month_year_select:after),
        :deep(.dp--year-select:after) {
            content: '';
            position: static;
            transform: translateY(1px);
            width: 0;
            height: 0;
            border-left: 4px solid transparent;
            border-right: 4px solid transparent;
            border-top: 5px solid #12122c;
        }

        :deep(.dp__month_year_select:hover),
        :deep(.dp__month_year_select:focus),
        :deep(.dp__month_year_select:focus-visible),
        :deep(.dp--year-select:hover),
        :deep(.dp--year-select:focus),
        :deep(.dp--year-select:focus-visible) {
            background: transparent;
            color: #12122c;
        }

        :deep(.dp__month_year_select[data-dp-element='overlay-month']) {
            justify-content: flex-start;
            text-align: left;
            margin-right: auto;
        }

        :deep(.dp__month_year_select[data-dp-element='overlay-year']) {
            justify-content: flex-end;
            text-align: right;
            margin-left: auto;
        }

        :deep(.dp__calendar_item .dp__cell_inner) {
            width: 32px;
            height: 32px;
            margin: 0 auto;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #12122c;
            font-weight: 400;
            font-size: 12px;
            line-height: 1;
        }

        :deep(.dp__calendar_header),
        :deep(.dp__calendar_row) {
            gap: 9.33px;
            justify-content: center;
        }

        :deep(.dp__calendar_row) {
            margin: 0 0 9.33px;
        }

        :deep(.dp__calendar_row:last-child) {
            margin-bottom: 0;
        }

        :deep(.dp__calendar_item),
        :deep(.dp__calendar_header_item) {
            flex-grow: 0;
            flex-shrink: 0;
        }

        :deep(.dp__calendar_header_item) {
            color: #8e8e9d;
            font-weight: 500;
            font-size: 12px;
            width: 32px;
            height: 32px;
            padding: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto;
        }

        :deep(.dp__calendar_header_separator) {
            display: none;
        }

        :deep(.dp__overlay_cell),
        :deep(.dp__overlay_cell_active),
        :deep(.dp__overlay_cell_disabled),
        :deep(.dp__overlay_cell_active_disabled) {
            border-radius: 80px;
            border: 1px solid transparent;
            transition: background-color 0.2s ease, border-color 0.2s ease,
                color 0.2s ease;
        }

        :deep(.dp__overlay_cell) {
            background-color: transparent;
            color: #12122c;
        }

        :deep(.dp__overlay_cell_active) {
            background-color: #f20b36;
            border-color: #f20b36;
            color: #fff;
        }

        :deep(.dp__overlay_cell:hover),
        :deep(.dp__overlay_cell:focus),
        :deep(.dp__overlay_cell:focus-visible),
        :deep(.dp__overlay_cell_active:hover),
        :deep(.dp__overlay_cell_active:focus),
        :deep(.dp__overlay_cell_active:focus-visible) {
            background-color: #ff0d3c;
            border-color: #ff0d3c;
            color: #fff;
            outline: none;
        }

        :deep(.dp__overlay_cell:active),
        :deep(.dp__overlay_cell_active:active) {
            background-color: #da0a31;
            border-color: #da0a31;
            color: #fff;
        }

        :deep(.dp__overlay_cell_disabled),
        :deep(.dp__overlay_cell_active_disabled),
        :deep(.dp__overlay_cell_disabled:hover),
        :deep(.dp__overlay_cell_active_disabled:hover) {
            background-color: #ebebee;
            border-color: #d4d3d9;
            color: #8e8e9d;
            cursor: not-allowed;
        }

        :deep(.dp__overlay_action svg) {
            display: none;
        }

        :deep(.dp__overlay_action:before) {
            content: '';
            width: 10px;
            height: 10px;
            border-left: 2px solid #12122c;
            border-bottom: 2px solid #12122c;
            transform: rotate(45deg);
            display: inline-block;
        }

        :deep(.dp__action_row) {
            padding-top: 8px;
        }

        :deep(.dp__overlay ~ .dp__action_row),
        :deep(.dp__menu:has(.dp__overlay) .dp__action_row) {
            display: none;
        }

        :deep(.dp__action_buttons) {
            width: 100%;
            flex: 1 1 100%;
            margin-inline-start: 0;
            justify-content: center;
        }

        :deep(.dp__action_buttons .dp__action_button.dp__action_cancel) {
            width: 100%;
            min-width: 0;
            height: 48px;
            border-radius: 80px;
            border: 1px solid #f20b36;
            background-color: #f20b36;
            color: #fff;
            font-weight: 600;
            font-size: 16px;
            line-height: 1;
            justify-content: center;
        }

        :deep(.dp__action_buttons .dp__action_button.dp__action_cancel:hover),
        :deep(.dp__action_buttons .dp__action_button.dp__action_cancel:focus),
        :deep(
                .dp__action_buttons
                    .dp__action_button.dp__action_cancel:focus-visible
            ) {
            background-color: #ff0d3c;
            border-color: #ff0d3c;
            color: #fff;
            outline: none;
        }

        :deep(.dp__action_buttons .dp__action_button.dp__action_cancel:active) {
            background-color: #da0a31;
            border-color: #da0a31;
            color: #fff;
        }

        :deep(.dp__action_buttons .dp__action_button.dp__action_cancel:disabled) {
            background-color: #ebebee;
            border-color: #d4d3d9;
            color: #8e8e9d;
            cursor: not-allowed;
        }

        :deep(.dp__calendar) {
            will-change: transform, opacity;
            backface-visibility: hidden;
            transform: translateZ(0);
        }

        :deep(.calendar-next-enter-active),
        :deep(.calendar-next-leave-active),
        :deep(.calendar-prev-enter-active),
        :deep(.calendar-prev-leave-active) {
            transition: transform 0.22s ease, opacity 0.22s ease;
        }

        :deep(.calendar-next-enter-from) {
            opacity: 0;
            transform: translate3d(0, 12px, 0);
        }

        :deep(.calendar-next-leave-to),
        :deep(.calendar-prev-enter-from) {
            opacity: 0;
            transform: translate3d(0, -12px, 0);
        }

        :deep(.calendar-prev-leave-to) {
            opacity: 0;
            transform: translate3d(0, 12px, 0);
        }

        :deep(.dp__calendar_item .dp__cell_inner.dp__today) {
            border: 1px solid #f20b36;
            color: #12122c;
            background: transparent;
        }

        :deep(.dp__calendar_item .dp__cell_inner.dp__active_date),
        :deep(.dp__calendar_item .dp__cell_inner.dp__range_start),
        :deep(.dp__calendar_item .dp__cell_inner.dp__range_end) {
            background: #f20b36;
            color: #fff;
            border: 1px solid #f20b36;
        }

        :deep(.dp__calendar_item .dp__cell_inner.dp__cell_disabled),
        :deep(.dp__calendar_item .dp__cell_inner.dp__cell_disabled:hover) {
            color: #8e8e9d;
            background: transparent;
            border-color: transparent;
            cursor: not-allowed;
        }
    }
}

@media (max-width: 767px) {
    .field {
        label {
            font-size: 14px;
            line-height: 14px;
        }
    }
}
</style>
